import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { Dropdown } from '@delmonicos/shared-design-system-react/src/components/dropdown/dropdown';
import { TextInput } from '@delmonicos/shared-design-system-react/src/components/input/text-input';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { Toggle } from '@delmonicos/shared-design-system-react/src/components/toggle/toggle';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { useUpdateTariffFormValidation } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/tariff/update-tariff.form-validation';
import { UpdateTariffModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/tariff/update-tariff.model';
import { TariffDimensionType } from '@delmonicos/shared-sdk/libs/cpms/src/libs/ocpi/src/enums/ocpi-protocol-enums';
// @ts-ignore
import data from 'currency-codes/data';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useTariffUIContext } from '@app/feature/tariff/tariff.context';

const TariffUpdateFormStep1InformationFragment = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { name, currency } = useUpdateTariffFormValidation();
  const { control } = useFormContext<UpdateTariffModel>();

  return (
    <Card flex={1} gap={GapEnum.M}>
      <AccentTitle
        icon="info-circle"
        text={t('$.tariff-update.step-1.information.title')}
      />

      <Controller
        name="tariff.name"
        rules={name}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t('delmonicos-shared-sdk.outputs-tariff.name')}
            placeholder={t('delmonicos-shared-sdk.outputs-tariff.name')}
            required={!!name.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) => {
              field.onChange(e.target.value === '' ? '' : e.target.value);
            }}
          />
        )}
      />

      <Controller
        name="tariff.currency"
        rules={currency}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Dropdown
            label={t('delmonicos-shared-sdk.outputs-tariff.currency')}
            required={!!currency.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(value) => field.onChange(value ?? '')}>
            {data.map((c: any) => (
              <Dropdown.Option key={c.code} value={c.code}>
                {c.code} - {c.currency}
              </Dropdown.Option>
            ))}
          </Dropdown>
        )}
      />
    </Card>
  );
};

const TariffUpdateFormStep1FlatFragment = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { flatValue, setFlatValue } = useTariffUIContext();
  const isFlat = flatValue !== undefined;

  const handleToggle = (checked: boolean) => {
    setFlatValue(checked ? 0 : undefined);
  };

  return (
    <Card gap={GapEnum.M}>
      <Stack gap={GapEnum.M} justifyContent="space-between" alignItems="center">
        <AccentTitle
          icon="home-03"
          text={t('$.tariff-update.step-1.flat.title')}
          variant={AccentVariantEnum.Secondary}
        />
        <Toggle id="flat" checked={isFlat} onChange={handleToggle} />
      </Stack>

      {isFlat && (
        <TextInput
          label={t('delmonicos-shared-sdk.outputs-price-component.price')}
          placeholder={t('delmonicos-shared-sdk.outputs-price-component.price')}
          required
          type="number"
          value={flatValue}
          onChange={(e) =>
            setFlatValue(
              e.target.value === '' ? undefined : e.target.valueAsNumber,
            )
          }
        />
      )}
    </Card>
  );
};

const TariffUpdateFormStep1PriceRangeFragment = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { isPriceRange, setIsPriceRange } = useTariffUIContext();
  const { setValue, getValues } = useFormContext<UpdateTariffModel>();

  const handlePriceChange = (value: boolean) => {
    setIsPriceRange(value);

    const emptyComponents = [
      { type: TariffDimensionType.ENERGY },
      { type: TariffDimensionType.TIME },
      { type: TariffDimensionType.PARKING_TIME },
    ];

    if (value) {
      const emptyElement = { priceComponents: emptyComponents };
      setValue('tariff.elements', [
        { ...emptyElement },
        { ...emptyElement },
        { ...emptyElement },
        { ...emptyElement },
      ] as any);
    } else {
      const current = getValues('tariff.elements.0.priceComponents') ?? [];
      const byType = (type: TariffDimensionType) =>
        current.find((pc) => pc.type === type);
      setValue('tariff.elements', [
        {
          priceComponents: [
            byType(TariffDimensionType.ENERGY) ?? {
              type: TariffDimensionType.ENERGY,
            },
            byType(TariffDimensionType.TIME) ?? {
              type: TariffDimensionType.TIME,
            },
            byType(TariffDimensionType.PARKING_TIME) ?? {
              type: TariffDimensionType.PARKING_TIME,
            },
          ],
        },
      ] as any);
    }
  };

  return (
    <Card gap={GapEnum.M}>
      <Stack gap={GapEnum.M} justifyContent="space-between" alignItems="center">
        <AccentTitle
          icon="clock"
          text={t('$.tariff-update.step-1.price-range.title')}
          variant={AccentVariantEnum.Accent2}
        />
        <Toggle
          id="price-range"
          checked={isPriceRange}
          onChange={handlePriceChange}
        />
      </Stack>
    </Card>
  );
};

export const TariffUpdateFormStep1Component = (): React.JSX.Element => {
  return (
    <Stack gap={GapEnum.M} flex={1} direction="column">
      <TariffUpdateFormStep1InformationFragment />
      <TariffUpdateFormStep1FlatFragment />
      <TariffUpdateFormStep1PriceRangeFragment />
    </Stack>
  );
};
